import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PlatformAuthResponse, PlatformUser } from '../models/platform.model';

const TOKEN_KEY = 'ma_platform_token';

@Injectable({ providedIn: 'root' })
export class PlatformAuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly apiUrl = `${environment.apiBaseUrl}/platform`;
  private readonly userSignal = signal<PlatformUser | null>(null);

  readonly currentUser = this.userSignal.asReadonly();
  readonly isAuthenticated = computed(() => this.userSignal() !== null);

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  async initializeSession(): Promise<void> {
    if (!this.getToken()) {
      return;
    }
    try {
      const user = await firstValueFrom(
        this.http.get<PlatformUser>(`${this.apiUrl}/auth/me`),
      );
      this.userSignal.set(user);
    } catch {
      this.clearSession();
    }
  }

  async login(email: string, password: string): Promise<PlatformUser> {
    const response = await firstValueFrom(
      this.http.post<PlatformAuthResponse>(`${this.apiUrl}/auth/login`, {
        email,
        password,
      }),
    );
    localStorage.setItem(TOKEN_KEY, response.accessToken);
    this.userSignal.set(response.user);
    return response.user;
  }

  logout(): void {
    this.clearSession();
    void this.router.navigate(['/platform/login']);
  }

  private clearSession(): void {
    localStorage.removeItem(TOKEN_KEY);
    this.userSignal.set(null);
  }
}
